/**
 * Seed categories: a short list of main categories covering most of what a
 * receipt is ever for, each with a handful of subcategories.
 *
 * Written on first launch, and upgraded once when this list changes shape (see
 * `planCategorySeedUpgrade`), so users are free to rename or delete them.
 */

import { normalizeSearchName } from './parse.js';
import type { Category, CategoryScope, ID, ReceiptItem, SuggestedCategory, Tag } from './types.js';

export interface CategorySeed {
  /** Stable slug, also used as the record id so seeding is idempotent. */
  slug: string;
  /**
   * English name for the AI model: what it is shown and answers with when it
   * files a receipt. The Swedish `name` is the user's label and may be renamed.
   */
  key: string;
  name: string;
  color: string;
  icon: string;
  scope: CategoryScope;
  /** The main category's slug, or `null` for a main category. */
  parent: string | null;
  /** Lowercase words that auto-assign this category to a line item; see `guessCategorySlug`. */
  match: string[];
}

/**
 * Bumped whenever `DEFAULT_CATEGORIES` changes in a way an existing install
 * should pick up. Clients store the version they last seeded or upgraded to.
 */
export const CATEGORY_SEED_VERSION = 4;

type SeedInput = Omit<CategorySeed, 'parent' | 'scope'> & { scope?: CategoryScope };

/** A main category followed by its subcategories, which inherit its slug as parent. */
function group(main: SeedInput, children: SeedInput[]): CategorySeed[] {
  return [
    { scope: 'both', ...main, parent: null },
    ...children.map((child) => ({ scope: 'both' as const, ...child, parent: main.slug })),
  ];
}

/*
 * Thirteen main categories, forty rows in all. A main category is a budget
 * call - what the totals show, since every line rolls up to one - and a
 * subcategory is there only when it answers a question people ask (takeaway or
 * restaurants, what the car costs, alcohol) without leaving them torn between
 * two of them. Restaurang & café, Hem & hushåll, Kläder & mode, Elektronik and
 * Bygg & trädgård have almost none.
 *
 * Anything narrower - a boat, a horse, a summer cottage - is a subcategory the
 * user adds under the right main category, or a tag.
 *
 * Every row is offered for both a whole receipt and a single line - a
 * toothpaste on a grocery receipt belongs under Hälsa & skönhet as much as a
 * pharmacy receipt does - except Pant, which is only ever a line.
 *
 * The key is all the model reading a receipt is shown, so it carries the rules
 * a name leaves unsaid: lotteries are entertainment, a child's clothes are the
 * child's. Slugs never change once shipped, so receipts filed under them stay
 * filed.
 */
export const DEFAULT_CATEGORIES: CategorySeed[] = [
  ...group({ slug: 'livsmedel', key: 'groceries', name: 'Livsmedel', color: '#3fa34d', icon: '🛒', match: ['livsmedel'] }, [
    { slug: 'frukt-gront', key: 'fruit_and_vegetables', name: 'Frukt & grönt', color: '#6fbf3f', icon: '🥬',
      match: ['banan', 'äpple', 'apelsin', 'päron', 'tomat', 'gurka', 'sallad', 'potatis', 'lök', 'rödlök', 'gullök',
              'vitlök', 'purjolök', 'morot', 'morötter', 'paprika', 'avokado', 'citron', 'lime', 'vindruvor', 'melon',
              'broccoli', 'blomkål', 'champinjon', 'spenat', 'zucchini', 'bär', 'blåbär', 'hallon', 'jordgubbar'] },
    { slug: 'mejeri', key: 'dairy_and_eggs', name: 'Mejeri & ägg', color: '#f0c419', icon: '🥛',
      match: ['mjölk', 'mellanmjölk', 'grädde', 'yoghurt', 'fil', 'filmjölk', 'gräddfil', 'smör', 'ost', 'hushållsost',
              'prästost', 'ägg', 'kvarg', 'creme fraiche', 'crème fraîche', 'margarin'] },
    { slug: 'kott-fisk', key: 'meat_and_fish', name: 'Kött, chark & fisk', color: '#c0392b', icon: '🥩',
      match: ['kyckling', 'köttfärs', 'nötfärs', 'fläsk', 'korv', 'bacon', 'skinka', 'lax', 'laxfilé', 'gravlax', 'torsk',
              'räkor', 'fisk', 'filé', 'falukorv', 'kassler', 'köttbullar'] },
    { slug: 'skafferi', key: 'pantry_and_frozen', name: 'Skafferi & fryst', color: '#c98a3c', icon: '🥫',
      match: ['pasta', 'ris', 'mjöl', 'socker', 'salt', 'olja', 'krossade tomater', 'bönor', 'linser', 'müsli',
              'flingor', 'havregryn', 'ketchup', 'senap', 'buljong', 'nudlar', 'marmelad', 'sylt',
              'fryst', 'glass', 'pizza', 'pommes'] },
    { slug: 'bageri', key: 'bread_and_bakery', name: 'Bröd & bageri', color: '#d4a373', icon: '🥖',
      match: ['bröd', 'limpa', 'frallor', 'knäckebröd', 'bulle', 'kaka', 'tortilla', 'baguette'] },
    { slug: 'dryck', key: 'drinks', name: 'Dryck', color: '#2980b9', icon: '🥤',
      match: ['läsk', 'juice', 'vatten', 'kaffe', 'bryggkaffe', 'tepåsar', 'saft', 'cola', 'soda', 'smoothie',
              'energidryck', 'lättöl'] },
    { slug: 'godis', key: 'sweets_and_snacks', name: 'Godis & snacks', color: '#e84393', icon: '🍬',
      match: ['godis', 'choklad', 'chips', 'nötter', 'lösgodis', 'kex', 'tuggummi'] },
    { slug: 'alkohol-tobak', key: 'alcohol_and_tobacco', name: 'Alkohol & tobak', color: '#7b241c', icon: '🍷',
      match: ['folköl', 'starköl', 'öl', 'vin', 'rödvin', 'vitt vin', 'cider', 'sprit', 'whisky', 'vodka',
              'snus', 'portionssnus', 'cigaretter', 'tobak', 'nikotin'] },
  ]),

  { slug: 'restaurang', key: 'restaurants_and_cafes', name: 'Restaurang & café', color: '#e67e22', icon: '🍽️', scope: 'both', parent: null,
    match: ['dagens', 'dagens lunch', 'förrätt', 'varmrätt', 'efterrätt', 'dricks', 'latte', 'caffe latte', 'cappuccino',
            'espresso', 'bakelse', 'kanelbulle', 'fika', 'hamburgare', 'burgare', 'burger', 'kebab', 'falafel', 'sushi',
            'meny', 'korv med bröd', 'leveransavgift'] },

  ...group({ slug: 'hem', key: 'home_and_household', name: 'Hem & hushåll', color: '#16a085', icon: '🏡',
    match: ['toapapper', 'toalettpapper', 'hushållspapper', 'disk', 'diskmedel', 'tvättmedel', 'sköljmedel', 'sopsäck',
            'avfallspåsar', 'rengöring', 'aluminiumfolie', 'plastfolie', 'plastpåsar', 'bärkasse', 'stearinljus',
            'värmeljus', 'glödlampa', 'led lampa', 'batteri', 'dammsugarpåsar', 'städning', 'hemstädning', 'fönsterputs',
            'möbel', 'soffa', 'fåtölj', 'stol', 'matbord', 'soffbord', 'skrivbord', 'hylla', 'förvaring', 'kudde',
            'kuddfodral', 'pläd', 'gardin', 'matta', 'lampa', 'vas', 'tavla', 'ljusstake', 'doftljus', 'handduk',
            'sängkläder', 'kastrull', 'stekpanna', 'kniv', 'skärbräda', 'tallrik', 'bestick', 'porslin', 'dricksglas',
            'kopp', 'mugg', 'serveringsfat'] }, [
    { slug: 'husdjur', key: 'pets', name: 'Husdjur', color: '#8d6e63', icon: '🐾',
      match: ['hundmat', 'kattmat', 'kattsand', 'hundgodis', 'djurfoder', 'foder', 'hästfoder', 'tuggben', 'veterinär'] },
  ]),

  { slug: 'bygg-tradgard', key: 'diy_and_garden', name: 'Bygg & trädgård', color: '#6e8b3d', icon: '🔨', scope: 'both', parent: null,
    match: ['skruv', 'spik', 'plugg', 'pensel', 'färg', 'spackel', 'silikon', 'tejp', 'beslag', 'tapet', 'kakel',
            'gips', 'gipsskiva', 'isolering', 'regel', 'verktyg', 'hammare', 'sågblad', 'borr', 'skruvdragare',
            'blomjord', 'planteringsjord', 'fröer', 'gräsfrö', 'gödsel', 'kruka', 'trädgård', 'grillkol'] },

  ...group({ slug: 'boende', key: 'housing', name: 'Boende', color: '#5d6d7e', icon: '🏠', match: [] }, [
    { slug: 'hyra', key: 'rent_and_fees', name: 'Hyra & avgifter', color: '#7b8a9a', icon: '🔑',
      match: ['hyra', 'hyresavi', 'månadsavgift', 'sotning'] },
    { slug: 'el-varme', key: 'electricity_water_and_heating', name: 'El, vatten & värme', color: '#f1c40f', icon: '💡',
      match: ['elförbrukning', 'elnät', 'elhandel', 'energiskatt', 'fjärrvärme', 'vattenavgift', 'pellets', 'ved',
              'eldningsolja'] },
  ]),

  ...group({ slug: 'abonnemang', key: 'subscriptions_and_insurance', name: 'Abonnemang & försäkring', color: '#8e44ad', icon: '🔁',
    match: ['prenumeration', 'abonnemang'] }, [
    { slug: 'mobil-internet', key: 'phone_internet_and_streaming', name: 'Telefon, internet & streaming', color: '#9b59b6', icon: '📶',
      match: ['mobilabonnemang', 'bredband', 'kontantkort', 'spotify', 'netflix', 'viaplay', 'disney+', 'hbo', 'icloud',
              'app store'] },
    { slug: 'forsakring', key: 'insurance', name: 'Försäkringar', color: '#6c3483', icon: '🛡️',
      match: ['försäkring'] },
  ]),

  ...group({ slug: 'transport', key: 'transport', name: 'Transport', color: '#34495e', icon: '🚗',
    match: ['körskola', 'körlektion', 'trafikskola', 'riskettan', 'halkbana'] }, [
    { slug: 'drivmedel', key: 'fuel_and_charging', name: 'Drivmedel & laddning', color: '#2c3e50', icon: '⛽',
      match: ['bensin', 'diesel', 'drivmedel', 'laddning', 'e85', 'hvo'] },
    { slug: 'kollektivtrafik', key: 'public_transport_and_taxi', name: 'Kollektivtrafik & taxi', color: '#2471a3', icon: '🚆',
      match: ['biljett', 'reskassa', 'månadsbiljett', 'taxi'] },
    { slug: 'parkering', key: 'parking_and_tolls', name: 'Parkering & vägavgifter', color: '#566573', icon: '🅿️',
      match: ['parkering', 'p-avgift', 'trängselskatt', 'vägtull', 'broavgift'] },
    { slug: 'fordon', key: 'vehicles_and_service', name: 'Fordon & service', color: '#839192', icon: '🔧',
      match: ['biltvätt', 'spolarvätska', 'däck', 'däckhotell', 'hjulskifte', 'oljebyte', 'motorolja', 'bilservice',
              'besiktning', 'kontrollbesiktning', 'fordonsskatt', 'torkarblad', 'cykel', 'cykelservice', 'elcykel'] },
    { slug: 'resor', key: 'trips_and_lodging', name: 'Resor & logi', color: '#0097a7', icon: '✈️',
      match: ['paketresa', 'hyrbil', 'flygbiljett', 'tågbiljett', 'bagage', 'hotell', 'övernattning', 'logi', 'vandrarhem'] },
  ]),

  { slug: 'klader', key: 'clothing_and_fashion', name: 'Kläder & mode', color: '#d35400', icon: '👕', scope: 'both', parent: null,
    match: ['tröja', 'byxor', 'jeans', 'chinos', 't-shirt', 'strumpor', 'jacka', 'klänning', 'kjol', 'skjorta',
            'kalsonger', 'trosor', 'underkläder', 'skor', 'sneakers', 'stövlar', 'sandaler', 'kängor', 'väska',
            'bälte', 'mössa', 'vantar', 'halsduk', 'smycke', 'klocka'] },

  { slug: 'elektronik', key: 'electronics', name: 'Elektronik & vitvaror', color: '#7f8c8d', icon: '💻', scope: 'both', parent: null,
    match: ['dator', 'laptop', 'iphone', 'surfplatta', 'ipad', 'laddare', 'usb', 'kabel', 'skärm', 'tangentbord',
            'hörlurar', 'högtalare', 'kamera', 'minneskort', 'grafikkort', 'tv', 'dammsugare', 'vattenkokare',
            'brödrost', 'mikrovågsugn', 'kaffebryggare', 'diskmaskin', 'tvättmaskin', 'kylskåp'] },

  ...group({ slug: 'halsa', key: 'health_and_beauty', name: 'Hälsa & skönhet', color: '#af7ac5', icon: '🌿', match: [] }, [
    { slug: 'hygien', key: 'personal_care_and_beauty', name: 'Hygien & skönhet', color: '#d2b4de', icon: '🧴',
      match: ['schampo', 'balsam', 'tvål', 'tandkräm', 'deodorant', 'bindor', 'tandborste', 'rakhyvel', 'hudkräm',
              'mascara', 'foundation', 'nagellack', 'parfym', 'hårfärg', 'frisör', 'klippning', 'massage'] },
    { slug: 'apotek', key: 'pharmacy_and_healthcare', name: 'Apotek & vård', color: '#e74c3c', icon: '💊',
      match: ['alvedon', 'ipren', 'panodil', 'plåster', 'vitamin', 'receptbelagt', 'näsdroppar', 'nässpray',
              'halstabletter', 'patientavgift', 'tandläkare', 'glasögon', 'synundersökning', 'kontaktlinser'] },
  ]),

  ...group({ slug: 'fritid', key: 'leisure_and_entertainment', name: 'Fritid & nöje', color: '#9b59b6', icon: '🎬', match: [] }, [
    { slug: 'noje', key: 'events_shows_and_lotteries', name: 'Nöje & evenemang', color: '#e91e63', icon: '🎟️',
      match: ['biobiljett', 'konsert', 'konsertbiljett', 'teater', 'entré', 'museum', 'triss', 'lotto', 'svenska spel',
              'atg', 'v75', 'skraplott'] },
    { slug: 'sport', key: 'sports_fitness_and_outdoors', name: 'Sport, träning & friluftsliv', color: '#2e86c1', icon: '⚽',
      match: ['skidor', 'liftkort', 'fiskespö', 'fiskekort', 'tält', 'sovsäck', 'gymkort', 'träningskort',
              'proteinpulver', 'padel', 'banhyra', 'greenfee'] },
    { slug: 'hobby', key: 'hobbies_books_and_games', name: 'Hobby, böcker & spel', color: '#7d3c98', icon: '🎨',
      match: ['garn', 'stickor', 'akrylfärg', 'pussel', 'brädspel', 'tidning', 'tidskrift', 'bok', 'pocket'] },
  ]),

  ...group({ slug: 'barn', key: 'children', name: 'Barn', color: '#ff8a65', icon: '🧸', match: [] }, [
    { slug: 'barnartiklar', key: 'childrens_clothes_gear_and_toys', name: 'Barnartiklar & leksaker', color: '#ffab91', icon: '🍼',
      match: ['blöjor', 'välling', 'napp', 'barnmat', 'leksak', 'barnkläder', 'barnstrumpor', 'barnvagn', 'lego'] },
    { slug: 'barnomsorg', key: 'childcare_and_activities', name: 'Förskola & aktiviteter', color: '#f4511e', icon: '🎒',
      match: ['förskoleavgift', 'barnomsorg', 'barnomsorgsavgift', 'simskola'] },
  ]),

  ...group({ slug: 'ovrigt', key: 'other', name: 'Övrigt', color: '#7f8c8d', icon: '📦',
    match: ['frakt', 'porto', 'frimärken', 'serviceavgift', 'expeditionsavgift', 'kursavgift', 'årsavgift'] }, [
    { slug: 'pant', key: 'bottle_deposit', name: 'Pant', color: '#95a5a6', icon: '♻️', scope: 'item', match: ['pant'] },
    { slug: 'presenter', key: 'gifts', name: 'Presenter & gåvor', color: '#e84393', icon: '🎁',
      match: ['presentkort', 'present', 'gåva', 'insamling'] },
  ]),
];

type MatchKind = 'whole' | 'end' | 'start';

/**
 * Where `keyword` stands in `text`, as a word or part of one: `whole`, the
 * `end` of a compound (`gräddfil`, `trallskruv`) or its `start` (`diskmedel`,
 * `tomater`). A keyword in the middle of a word is no match - `latte` in
 * `spånplatteskruv`, `skor` in `majskorn` - and one of three letters or
 * fewer has to be a word of its own, or `ägg` finds every `väggfärg`.
 */
function matchKind(text: string, keyword: string): MatchKind | null {
  const isLetter = (char: string | undefined) => char !== undefined && /\p{L}/u.test(char);
  let best: MatchKind | null = null;
  for (let at = text.indexOf(keyword); at >= 0; at = text.indexOf(keyword, at + 1)) {
    const start = !isLetter(text[at - 1]);
    const end = !isLetter(text[at + keyword.length]);
    const kind: MatchKind | null = start && end ? 'whole' : keyword.length <= 3 ? null : end ? 'end' : start ? 'start' : null;
    if (kind && (!best || MATCH_RANK[kind] > MATCH_RANK[best])) best = kind;
  }
  return best;
}

/**
 * A whole word beats the end of a compound, which beats its start: Swedish
 * puts the head last, so `potatischips` is chips and `mjölkchoklad` chocolate.
 */
const MATCH_RANK: Record<MatchKind, number> = { start: 0, end: 1, whole: 2 };

/** The keywords as `normalizeSearchName` leaves a line: `p-avgift` is looked for as `p avgift`. */
let normalizedKeywords: { slug: string; keyword: string }[] | null = null;
function seedKeywords(): { slug: string; keyword: string }[] {
  normalizedKeywords ??= DEFAULT_CATEGORIES.flatMap((seed) =>
    seed.match.map((word) => ({ slug: seed.slug, keyword: normalizeSearchName(word) })));
  return normalizedKeywords;
}

/**
 * Best-effort category guess for a line item, by keyword, from its
 * `normalizeSearchName`. Returns the seed slug or `null`. Only used to
 * pre-fill; the user always has the last word, and a wrong guess costs them
 * more than none, so a keyword has to stand where a word would.
 */
export function guessCategorySlug(searchName: string): string | null {
  return guessCategorySlugs(searchName)[0] ?? null;
}

/**
 * Every seed slug a keyword in `searchName` points at, best first: the
 * better-placed keyword wins, then the more specific one, then the earlier
 * seed. The first is {@link guessCategorySlug}'s guess; the rest are what
 * else the line could be, for suggestions.
 */
export function guessCategorySlugs(searchName: string): string[] {
  if (!searchName) return [];
  const best = new Map<string, { rank: number; length: number; order: number }>();
  for (const [order, { slug, keyword }] of seedKeywords().entries()) {
    const kind = matchKind(searchName, keyword);
    if (!kind) continue;
    const rank = MATCH_RANK[kind];
    const current = best.get(slug);
    if (!current || rank > current.rank || (rank === current.rank && keyword.length > current.length)) {
      best.set(slug, { rank, length: keyword.length, order });
    }
  }
  return [...best.entries()]
    .sort(([, a], [, b]) => b.rank - a.rank || b.length - a.length || a.order - b.order)
    .map(([slug]) => slug);
}

/**
 * The id of a seeded category, derived from its slug.
 *
 * Seeded categories are created independently on every device rather than
 * synced, so both sides must arrive at the same id or a receipt filed under
 * "Livsmedel" on the phone would point at nothing on the server. The client and
 * the server's extraction worker both derive it here, so the rule they agree on
 * is written down once instead of being a template literal repeated in two
 * packages and held together by a comment.
 */
export function seedCategoryId(slug: string): string {
  return `seed-category-${slug}`;
}

/** The fields of a category that seeding decides; sync metadata is the client's. */
export type SeededCategoryFields = Pick<Category, 'id' | 'name' | 'color' | 'icon' | 'parentId' | 'scope' | 'sortOrder'>;

/** Every default category as the row a fresh install writes. */
export function seedCategoryRows(): SeededCategoryFields[] {
  return DEFAULT_CATEGORIES.map((seed, index) => ({
    id: seedCategoryId(seed.slug),
    name: seed.name,
    color: seed.color,
    icon: seed.icon,
    parentId: seed.parent ? seedCategoryId(seed.parent) : null,
    scope: seed.scope,
    sortOrder: index,
  }));
}

/**
 * Every category a receipt is filed under, its own first, each once. The
 * additional ones never repeat the first, whatever an older client left.
 */
export function receiptCategoryIds(receipt: { categoryId: ID | null; additionalCategoryIds?: readonly ID[] }): ID[] {
  const ids = [receipt.categoryId, ...(receipt.additionalCategoryIds ?? [])].filter((id): id is ID => Boolean(id));
  return [...new Set(ids)];
}

// --- choosing a category with an AI model ----------------------------------

/**
 * A category as offered to the model reading a receipt: a key to answer with,
 * where it sits in the tree, and what it may be used for. No ids and no
 * user-facing names travel with it; it is what the server proxy is sent too.
 */
export interface CategoryOption {
  key: string;
  /** The main category's key, or `null` for a main category. */
  parentKey: string | null;
  scope: CategoryScope;
}

/** A {@link CategoryOption} and the category it stands for. */
export interface CategoryChoice extends CategoryOption {
  id: ID;
}

/**
 * The live categories as the model is offered them, in tree order.
 *
 * A seeded category is offered under its English key whatever the user has
 * renamed it to; one the user created has no key but its own name. Two rows
 * under the same key (a user category named like a seed) keep the first.
 */
export function categoryChoices(rows: readonly Pick<Category, 'id' | 'name' | 'parentId' | 'scope' | 'sortOrder'>[]): CategoryChoice[] {
  const seedKeys = new Map(DEFAULT_CATEGORIES.map((seed) => [seedCategoryId(seed.slug), seed.key]));
  const keyOf = new Map<ID, string>();
  const taken = new Set<string>();
  for (const { category } of orderCategoryTree(rows)) {
    const key = (seedKeys.get(category.id) ?? category.name).trim().replace(/\s+/g, ' ');
    if (!key || taken.has(key.toLowerCase())) continue;
    taken.add(key.toLowerCase());
    keyOf.set(category.id, key);
  }

  const out: CategoryChoice[] = [];
  for (const { category } of orderCategoryTree(rows)) {
    const key = keyOf.get(category.id);
    if (!key) continue;
    out.push({
      id: category.id,
      key,
      parentKey: category.parentId ? (keyOf.get(category.parentId) ?? null) : null,
      scope: category.scope,
    });
  }
  return out;
}

/** The notes `formatCategoryList` puts after a key, which a model sometimes copies into its answer. */
const SCOPE_NOTE = /\s*\((?:lines only|whole receipt only)\)$/;
/** What a model puts between a main category and its subcategory when it names both. */
const PATH_SEPARATOR = /^[\s.>:/|–-]+/;

/**
 * The choice an answer names. A key as listed comes first, case and spacing
 * forgiven. Then two slips in the format, never in the choice: a copied
 * scope note (`bottle_deposit (lines only)`), and a subcategory written
 * under its own main category (`groceries.dairy_and_eggs`,
 * `Boende - El, vatten & värme`, `childrenchildcare_and_activities`).
 */
function choiceNamed(answer: string, choices: readonly CategoryChoice[]): CategoryChoice | null {
  const byKey = (text: string) => (text ? (choices.find((entry) => entry.key.toLowerCase() === text) ?? null) : null);
  const wanted = answer.trim().replace(/\s+/g, ' ').toLowerCase();
  const listed = byKey(wanted);
  if (listed) return listed;
  const bare = wanted.replace(SCOPE_NOTE, '');
  const noted = byKey(bare);
  if (noted) return noted;
  for (const choice of choices) {
    const parent = choice.parentKey?.toLowerCase();
    if (!parent || !bare.startsWith(parent)) continue;
    if (bare.slice(parent.length).replace(PATH_SEPARATOR, '') === choice.key.toLowerCase()) return choice;
  }
  return null;
}

/**
 * The category a model's answer names, or `null`.
 *
 * The model is told to answer only with a key from the list, and anything else
 * - an invented category, a translation, a subcategory under a main category
 * that is not its own, a key meant only for the other kind of row - is
 * dropped rather than guessed at. Case, spacing and the slips in the format
 * that `choiceNamed` lists are forgiven; spelling is not.
 */
export function resolveCategoryKey(
  answer: unknown,
  choices: readonly CategoryChoice[],
  target: 'receipt' | 'item',
): ID | null {
  if (typeof answer !== 'string') return null;
  const choice = choiceNamed(answer, choices);
  if (!choice) return null;
  return choice.scope === 'both' || choice.scope === target ? choice.id : null;
}

/**
 * What each main category of the seed list holds, by its key. Written for the
 * seed list as a whole, not for any one test set's misses.
 */
export const MAIN_CATEGORY_NOTES: ReadonlyMap<string, string> = new Map([
  ['groceries', 'food and drink bought in a shop to have at home, ready meals and alcohol and tobacco included'],
  ['restaurants_and_cafes', 'food and drink served by a restaurant, café, bar, kiosk or takeaway, whatever it is; ready meals and salad bars bought in a grocery shop are groceries'],
  ['home_and_household', 'running and furnishing the home: cleaning and consumables, furniture, decor, kitchenware, home textiles, pets'],
  [
    'diy_and_garden',
    'building, renovating and repairing home and garden: tools and blades, paint, tape, filler, screws, timber, sheeting, plumbing and electrical parts, plants and soil',
  ],
  ['housing', 'paying for where you live: rent, fees, electricity, water, heating'],
  ['subscriptions_and_insurance', 'recurring plans: phone, internet, streaming, apps, insurance'],
  ['transport', 'getting around and trips away (daily commuting is public transport; long-distance and overnight trips are travel): fuel, charging, public transport, taxi, parking, tolls, the car and its service and parts, flights, long-distance trains, hotels and lodging'],
  ['clothing_and_fashion', 'clothes, shoes, bags and accessories'],
  ['electronics', 'computers, phones, TVs, white goods and small appliances, cables, chargers and their accessories'],
  ['health_and_beauty', 'hygiene, cosmetics, medicine, pharmacy and healthcare'],
  ['leisure_and_entertainment', 'events, cinema, lotteries, sport and outdoor gear, hobbies, books, games'],
  ['children', "children's clothes, gear and toys, childcare and activities"],
  ['other', 'deposits, gifts, and services, fees and anything else that fits nowhere'],
]);

/** A category list as a filing call can be offered it, described (see {@link describedCategories}). */
export interface DescribedCategories {
  /** The list with each seeded category under its Swedish seed name. */
  categories: CategoryOption[];
  /** A line on what each main category of the list holds, under the name it is offered by. */
  notes: string;
  /** An answer that names an offered category, as the key the list had; anything else as it was. */
  toKey: (answer: unknown) => unknown;
}

/**
 * The list for a text-only filing call, described: each seeded category under
 * its Swedish seed name rather than its English key, and a note on what each
 * main category holds. A small local model files far better so - Gemma 4 E4B's
 * line main groups went from 73.5% to 90.6% on the photo fixtures, and up on
 * every other set (idea: distilled line categorizer, results 2026-10-11). It
 * costs about 340 tokens of system prompt, the same on every call.
 *
 * A seed whose Swedish name a category the user made already goes by stays
 * under its key. Answers come back through `toKey`, so everything after the
 * call still sees the list `categoryChoices` made.
 */
export function describedCategories(categories: readonly CategoryOption[]): DescribedCategories {
  const seedNames = new Map(DEFAULT_CATEGORIES.map((seed) => [seed.key, seed.name]));
  const taken = new Set(categories.map((category) => category.key.toLowerCase()));
  const names = new Map<string, string>();
  for (const { key } of categories) {
    const name = seedNames.get(key);
    if (!name) continue;
    if (name.toLowerCase() !== key.toLowerCase() && taken.has(name.toLowerCase())) continue;
    taken.add(name.toLowerCase());
    names.set(key, name);
  }
  const offered = (key: string) => names.get(key) ?? key;

  const choices: CategoryChoice[] = categories.map((category) => ({
    ...category,
    id: category.key,
    key: offered(category.key),
    parentKey: category.parentKey ? offered(category.parentKey) : null,
  }));
  const notes = categories
    .filter((category) => !category.parentKey && MAIN_CATEGORY_NOTES.has(category.key))
    .map((category) => `${offered(category.key)}: ${MAIN_CATEGORY_NOTES.get(category.key)}`)
    .join('\n');
  return {
    categories: choices.map(({ key, parentKey, scope }) => ({ key, parentKey, scope })),
    notes,
    toKey: (answer) => (typeof answer === 'string' ? (choiceNamed(answer, choices)?.id ?? answer) : answer),
  };
}

/** Bounds on a category list from outside, so a request cannot inflate a prompt without limit. */
const MAX_CATEGORY_OPTIONS = 300;
const MAX_CATEGORY_KEY_LENGTH = 80;

/**
 * A category list as a device sends it to the server proxy, checked: entries
 * that are not a {@link CategoryOption} are dropped, keys are bounded, and
 * the list is cut at a few hundred.
 */
export function parseCategoryOptions(value: unknown): CategoryOption[] {
  if (!Array.isArray(value)) return [];
  const out: CategoryOption[] = [];
  for (const entry of value.slice(0, MAX_CATEGORY_OPTIONS)) {
    if (!entry || typeof entry !== 'object') continue;
    const { key, parentKey, scope } = entry as Record<string, unknown>;
    if (typeof key !== 'string' || !key.trim()) continue;
    out.push({
      key: key.trim().replace(/\s+/g, ' ').slice(0, MAX_CATEGORY_KEY_LENGTH),
      parentKey: typeof parentKey === 'string' && parentKey.trim() ? parentKey.trim().slice(0, MAX_CATEGORY_KEY_LENGTH) : null,
      scope: scope === 'receipt' || scope === 'item' ? scope : 'both',
    });
  }
  return out;
}

/**
 * The categories a model's list of answers names, each once, in its order,
 * leaving out `except` and anything not on the list (see {@link resolveCategoryKey}).
 */
export function resolveCategoryKeys(
  answers: readonly unknown[],
  choices: readonly CategoryChoice[],
  target: 'receipt' | 'item',
  except: readonly (ID | null)[] = [],
): ID[] {
  const out: ID[] = [];
  for (const answer of answers) {
    const id = resolveCategoryKey(answer, choices, target);
    if (id && !except.includes(id) && !out.includes(id)) out.push(id);
  }
  return out;
}

/** At most this many alternatives are kept on a line, and offered for it. */
export const MAX_LINE_SUGGESTIONS = 3;

/**
 * The model's alternatives for a line as the categories they name: each once,
 * in its order, only those a line may be filed under, never `except` (the
 * line's own), at most {@link MAX_LINE_SUGGESTIONS}. What a reading stores as
 * `ReceiptItem.suggestedCategories`.
 */
export function resolveSuggestedCategories(
  alternatives: readonly { key: string; score: number | null }[],
  choices: readonly CategoryChoice[],
  except: ID | null,
): SuggestedCategory[] {
  const out: SuggestedCategory[] = [];
  for (const { key, score } of alternatives) {
    const id = resolveCategoryKey(key, choices, 'item');
    if (!id || id === except || out.some((entry) => entry.id === id)) continue;
    out.push({ id, score });
    if (out.length === MAX_LINE_SUGGESTIONS) break;
  }
  return out;
}

/**
 * Below this a model's alternative is not offered. Its confidence is poorly
 * calibrated, so the number only orders and floors; it is never shown.
 */
export const SUGGESTION_SCORE_FLOOR = 0.15;

/** Where a suggestion came from, for the row's subtitle. */
export type LineSuggestionSource = 'history' | 'model' | 'keyword' | 'receipt';

export interface LineSuggestion {
  id: ID;
  source: LineSuggestionSource;
}

/**
 * The categories worth offering first when someone corrects a line, at most
 * {@link MAX_LINE_SUGGESTIONS}, from the strongest signal down:
 *
 * 1. `history`: what this household filed the same product (`searchName`)
 *    under last time, on another receipt. Needs no model at all.
 * 2. The model's alternatives (`suggestedCategories`), by score, above
 *    {@link SUGGESTION_SCORE_FLOOR}.
 * 3. Keyword matches on the name (`guessCategorySlugs`), among the seeded
 *    categories that still exist.
 * 4. The other categories the receipt is filed under.
 *
 * Never a category the line already counts under - its own, or the
 * receipt's, which the sheet offers as "Som kvittot" - nor one that is gone
 * or only for whole receipts. Nothing left means no suggestions, not padding.
 */
export function suggestLineCategories(
  item: Pick<ReceiptItem, 'categoryId' | 'searchName' | 'suggestedCategories'>,
  receipt: { categoryId: ID | null; additionalCategoryIds?: readonly ID[] },
  categories: readonly Pick<Category, 'id' | 'scope'>[],
  history: ID | null,
): LineSuggestion[] {
  const offered = new Set(categories.filter((category) => category.scope !== 'receipt').map((category) => category.id));
  const out: LineSuggestion[] = [];
  const add = (id: ID | null | undefined, source: LineSuggestionSource) => {
    if (out.length >= MAX_LINE_SUGGESTIONS || !id || !offered.has(id)) return;
    if (id === item.categoryId || id === receipt.categoryId || out.some((entry) => entry.id === id)) return;
    out.push({ id, source });
  };

  add(history, 'history');
  const fromModel = (item.suggestedCategories ?? [])
    .filter((entry) => entry.score === null || entry.score >= SUGGESTION_SCORE_FLOOR)
    .map((entry, index) => ({ entry, index }))
    .sort((a, b) => (b.entry.score ?? -1) - (a.entry.score ?? -1) || a.index - b.index);
  for (const { entry } of fromModel) add(entry.id, 'model');
  for (const slug of guessCategorySlugs(item.searchName)) add(seedCategoryId(slug), 'keyword');
  for (const id of receipt.additionalCategoryIds ?? []) add(id, 'receipt');
  return out;
}

// --- upgrading an earlier seed ---------------------------------------------

/**
 * The first seed list, as it was written: nineteen flat categories, mostly
 * grocery aisles. A row still carrying its original name is taken to be
 * untouched, and is safe to rename, recolour or retire.
 */
const LEGACY_SEEDS: Record<string, { name: string; scope: CategoryScope }> = {
  livsmedel: { name: 'Livsmedel', scope: 'both' },
  'frukt-gront': { name: 'Frukt & grönt', scope: 'item' },
  mejeri: { name: 'Mejeri & ägg', scope: 'item' },
  brod: { name: 'Bröd & bageri', scope: 'item' },
  'kott-fisk': { name: 'Kött, chark & fisk', scope: 'item' },
  skafferi: { name: 'Skafferi', scope: 'item' },
  fryst: { name: 'Fryst', scope: 'item' },
  dryck: { name: 'Dryck', scope: 'item' },
  'godis-snacks': { name: 'Godis & snacks', scope: 'item' },
  hushall: { name: 'Hushåll & städ', scope: 'item' },
  hygien: { name: 'Hygien & apotek', scope: 'item' },
  pant: { name: 'Pant', scope: 'item' },
  restaurang: { name: 'Restaurang & café', scope: 'receipt' },
  transport: { name: 'Transport & drivmedel', scope: 'receipt' },
  hem: { name: 'Hem & inredning', scope: 'receipt' },
  elektronik: { name: 'Elektronik', scope: 'receipt' },
  klader: { name: 'Kläder & skor', scope: 'receipt' },
  fritid: { name: 'Fritid & nöje', scope: 'receipt' },
  ovrigt: { name: 'Övrigt', scope: 'both' },
};

/** Legacy slugs with no place in the current list, and the category that absorbs each. */
const RETIRED_SEEDS: Record<string, string> = {
  brod: 'skafferi',
  fryst: 'skafferi',
  'godis-snacks': 'dryck',
};

/**
 * The rows of the second list (version 2: thirteen main categories, fifty
 * rows) that this one renames, moves or retires, as they were written: the
 * name, and the main category each sat under.
 */
const SECOND_SEEDS: Record<string, { name: string; parent: string }> = {
  inredning: { name: 'Möbler & inredning', parent: 'hem' },
  'bygg-tradgard': { name: 'Bygg & trädgård', parent: 'hem' },
  fordon: { name: 'Bilvård & service', parent: 'transport' },
  sport: { name: 'Sport & friluftsliv', parent: 'fritid' },
  plagg: { name: 'Kläder', parent: 'klader' },
  skor: { name: 'Skor & accessoarer', parent: 'klader' },
  datorer: { name: 'Datorer & mobiler', parent: 'elektronik' },
  hemelektronik: { name: 'Ljud, bild & apparater', parent: 'elektronik' },
  traning: { name: 'Träning & gym', parent: 'halsa' },
};

/**
 * Second-list subcategories that left people torn between them and answered
 * no budget question, and the category that absorbs each.
 */
const RETIRED_SECOND_SEEDS: Record<string, string> = {
  plagg: 'klader',
  skor: 'klader',
  datorer: 'elektronik',
  hemelektronik: 'elektronik',
  traning: 'sport',
};

/**
 * The rows of the third list (version 3: fourteen main categories, forty-six
 * rows) that this one renames or moves, as they were written. `parent` is
 * `null` for a row that was a main category.
 */
const THIRD_SEEDS: Record<string, { name: string; parent: string | null }> = {
  dryck: { name: 'Dryck & godis', parent: 'livsmedel' },
  elektronik: { name: 'Elektronik', parent: null },
  'mobil-internet': { name: 'Mobil & internet', parent: 'abonnemang' },
  resor: { name: 'Resor', parent: null },
};

/**
 * Third-list rows folded into a neighbour because the line between them was
 * thin (a train ticket against a commuter ticket, a cleaning cloth against a
 * tea towel), the names each may have gone by, and the category that absorbs it.
 */
const RETIRED_THIRD_SEEDS: Record<string, { names: string[]; replacement: string }> = {
  resebiljetter: { names: ['Flyg & tåg'], replacement: 'resor' },
  hotell: { names: ['Hotell & logi'], replacement: 'resor' },
  streaming: { names: ['Streaming & appar'], replacement: 'mobil-internet' },
  cafe: { names: ['Café & fika'], replacement: 'restaurang' },
  snabbmat: { names: ['Snabbmat & takeaway'], replacement: 'restaurang' },
  tjanster: { names: ['Tjänster & avgifter'], replacement: 'ovrigt' },
  hushall: { names: ['Städ & förbrukning', 'Hushåll & städ'], replacement: 'hem' },
  inredning: { names: ['Inredning & husgeråd', 'Möbler & inredning'], replacement: 'hem' },
};

/** Every id an upgrade needs to look at: current seeds and retired ones. */
export function seedCategoryIdsToInspect(): ID[] {
  const slugs = new Set([
    ...DEFAULT_CATEGORIES.map((seed) => seed.slug),
    ...Object.keys(LEGACY_SEEDS),
    ...Object.keys(SECOND_SEEDS),
    ...Object.keys(THIRD_SEEDS),
    ...Object.keys(RETIRED_THIRD_SEEDS),
  ]);
  return [...slugs].map(seedCategoryId);
}

export interface CategorySeedUpgrade {
  /** Seeds with no row at all, to be written as new. */
  create: SeededCategoryFields[];
  /** Existing seeded rows and the fields that change on each. */
  update: { id: ID; patch: Partial<SeededCategoryFields> }[];
  /**
   * Untouched legacy rows with no place in the new list. Every receipt and line
   * filed under one moves to `replacementId`, then the row is deleted.
   */
  retire: { id: ID; replacementId: ID }[];
}

/**
 * What it takes to bring an install seeded with an earlier list up to this one.
 *
 * `existing` holds whatever rows exist for `seedCategoryIdsToInspect()`,
 * deleted ones included. The rules, all in favour of what the user did:
 *
 * - A seed the user deleted stays deleted.
 * - A seed still under its first-list name takes the new name, colour, icon,
 *   scope and position; one still under its second-list name takes the new
 *   name, and keeps its place in an order the user may have arranged. A
 *   renamed one keeps all of that.
 * - Any seed without a main category is moved under its new one, as long as
 *   that main category is alive, and one still under its second-list main
 *   category moves to its new place. One the user moved stays.
 * - A retired seed is folded into its replacement only if untouched; a
 *   renamed one is the user's own category now, and stays.
 */
export function planCategorySeedUpgrade(existing: ReadonlyMap<ID, Category>): CategorySeedUpgrade {
  const plan: CategorySeedUpgrade = { create: [], update: [], retire: [] };
  const rows = seedCategoryRows();

  const alive = (id: ID): boolean => {
    const row = existing.get(id);
    return row ? row.deletedAt === 0 : rows.some((seed) => seed.id === id);
  };

  for (const [index, target] of rows.entries()) {
    const slug = DEFAULT_CATEGORIES[index]?.slug ?? '';
    const parentId = target.parentId && alive(target.parentId) ? target.parentId : null;
    const row = existing.get(target.id);

    if (!row) {
      plan.create.push({ ...target, parentId });
      continue;
    }
    if (row.deletedAt !== 0) continue;

    const patch: Partial<SeededCategoryFields> = {};
    const legacy = LEGACY_SEEDS[slug];
    const second = SECOND_SEEDS[slug];
    const third = THIRD_SEEDS[slug];
    if (third && row.name === third.name && row.name !== target.name) {
      patch.name = target.name;
    } else if (legacy && row.name === legacy.name) {
      if (row.name !== target.name) patch.name = target.name;
      if (row.color !== target.color) patch.color = target.color;
      if (row.icon !== target.icon) patch.icon = target.icon;
      if (row.scope === legacy.scope && row.scope !== target.scope) patch.scope = target.scope;
      if (row.sortOrder !== target.sortOrder) patch.sortOrder = target.sortOrder;
    } else if (second && row.name === second.name && row.name !== target.name) {
      patch.name = target.name;
    }
    if (row.parentId === null && parentId !== null) patch.parentId = parentId;
    else if (second && row.parentId === seedCategoryId(second.parent) && row.parentId !== parentId) patch.parentId = parentId;
    if (Object.keys(patch).length > 0) plan.update.push({ id: row.id, patch });
  }

  const retired = [
    ...Object.entries(RETIRED_SEEDS).map(([slug, replacement]) => ({ slug, replacement, names: [LEGACY_SEEDS[slug]?.name] })),
    ...Object.entries(RETIRED_SECOND_SEEDS).map(([slug, replacement]) => ({ slug, replacement, names: [SECOND_SEEDS[slug]?.name] })),
    ...Object.entries(RETIRED_THIRD_SEEDS).map(([slug, { names, replacement }]) => ({ slug, replacement, names })),
  ];
  for (const { slug, replacement, names } of retired) {
    const row = existing.get(seedCategoryId(slug));
    const replacementId = seedCategoryId(replacement);
    if (!row || row.deletedAt !== 0 || !names.includes(row.name)) continue;
    if (!alive(replacementId)) continue;
    plan.retire.push({ id: row.id, replacementId });
  }

  return plan;
}

// --- the tree -------------------------------------------------------------

type TreeNode = Pick<Category, 'id' | 'parentId' | 'sortOrder' | 'name'>;

/**
 * Categories in the order a list shows them: each main category followed by
 * its subcategories, both by `sortOrder`. A row whose parent is missing from
 * `rows` - deleted, or never synced - is shown as a main category.
 */
export function orderCategoryTree<T extends TreeNode>(rows: readonly T[]): { category: T; depth: number }[] {
  const ids = new Set(rows.map((row) => row.id));
  const byParent = new Map<ID | null, T[]>();
  for (const row of rows) {
    const parent = row.parentId && ids.has(row.parentId) && row.parentId !== row.id ? row.parentId : null;
    const siblings = byParent.get(parent) ?? [];
    siblings.push(row);
    byParent.set(parent, siblings);
  }

  const out: { category: T; depth: number }[] = [];
  const seen = new Set<ID>();
  const visit = (parent: ID | null, depth: number) => {
    const children = (byParent.get(parent) ?? []).sort(
      (a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name, 'sv'),
    );
    for (const child of children) {
      // A cycle (A under B under A) arrives through sync with nothing to stop it.
      if (seen.has(child.id)) continue;
      seen.add(child.id);
      out.push({ category: child, depth });
      visit(child.id, depth + 1);
    }
  };
  visit(null, 0);
  // Anything only reachable through a cycle is still shown, at the top.
  for (const row of rows) if (!seen.has(row.id)) out.push({ category: row, depth: 0 });
  return out;
}

/** The main category a category rolls up to: itself, or its topmost live ancestor. */
export function mainCategoryId(id: ID, rows: readonly Pick<Category, 'id' | 'parentId'>[]): ID {
  const byId = new Map(rows.map((row) => [row.id, row]));
  let current = id;
  const seen = new Set<ID>([current]);
  for (;;) {
    const parent = byId.get(current)?.parentId;
    if (!parent || !byId.has(parent) || seen.has(parent)) return current;
    seen.add(parent);
    current = parent;
  }
}

/**
 * The given ids and every category beneath them, so filtering on a main
 * category also finds what is filed under its subcategories.
 */
export function withSubcategories(ids: readonly ID[], rows: readonly Pick<Category, 'id' | 'parentId'>[]): ID[] {
  const out = new Set(ids);
  let grew = true;
  while (grew) {
    grew = false;
    for (const row of rows) {
      if (row.parentId && out.has(row.parentId) && !out.has(row.id)) {
        out.add(row.id);
        grew = true;
      }
    }
  }
  return [...out];
}

/**
 * Categories matching what someone typed, in tree order, each with its main
 * category for context.
 *
 * Matches the name, and for a seeded category the words it guesses from as
 * well, so "parkering" or "tandkräm" finds where such a thing belongs. A main
 * category that matches brings its subcategories along: typing "Livsmedel"
 * is asking what is in it.
 */
export function searchCategories<T extends TreeNode>(
  rows: readonly T[],
  query: string,
): { category: T; parent: T | null }[] {
  const needle = query.trim().toLocaleLowerCase('sv');
  if (needle.length === 0) return [];

  const keywords = new Map<ID, string[]>();
  for (const { slug, keyword } of seedKeywords()) {
    const id = seedCategoryId(slug);
    keywords.set(id, [...(keywords.get(id) ?? []), keyword]);
  }
  // The start of a keyword's word ("tandkr", "lunch") finds it, so "hotell"
  // is not a däckhotell; a text holding a keyword finds it only where
  // `guessCategorySlug` would, so "väggfärg" does not offer Mejeri & ägg.
  const text = normalizeSearchName(needle);
  const byId = new Map(rows.map((row) => [row.id, row]));
  const hits = (row: T): boolean =>
    row.name.toLocaleLowerCase('sv').includes(needle) ||
    (text.length >= 3 && (keywords.get(row.id) ?? []).some((word) => word.split(' ').some((part) => part.startsWith(text)) || matchKind(text, word) !== null));

  const matched = new Set<ID>();
  for (const row of rows) if (hits(row)) matched.add(row.id);
  for (const row of rows) if (row.parentId && matched.has(row.parentId)) matched.add(row.id);

  return orderCategoryTree(rows)
    .filter(({ category }) => matched.has(category.id))
    .map(({ category }) => ({
      category,
      parent: category.parentId ? (byId.get(category.parentId) ?? null) : null,
    }));
}

/**
 * The order tags are listed in, on every client.
 *
 * By `sortOrder` where the user has arranged them, then by name. A tag with no
 * `sortOrder` - every tag until someone first moves one, and any tag written
 * by a client that does not know the field - sorts after the ordered ones,
 * alphabetically, which is the order tags always had.
 */
export function compareTags(a: Pick<Tag, 'name' | 'sortOrder'>, b: Pick<Tag, 'name' | 'sortOrder'>): number {
  return (
    (a.sortOrder ?? Number.POSITIVE_INFINITY) - (b.sortOrder ?? Number.POSITIVE_INFINITY) ||
    a.name.localeCompare(b.name, 'sv')
  );
}
